import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getMetaDataForFilter, getAllAssessmentsForPaperReview,getTeacherAssignedAssessments,getDEOPAssignedAssessments } from '../../service/assessSections/assessSectionsService/assessSectionsService';


const AssessLibSectionsContext = createContext(null);
export const useAssessmentLibrarySections = () => useContext(AssessLibSectionsContext);


// -------------------------------------------------------------------------
// Fetch metadata once on mount
// ------------------------------------------------------------------------
const EMPTY_OPTIONS = {
  gradeOptions: [],
  subjectOptions: [],
  languageOptions: [],
  examCategoryOptions: [],
  termOptions: [],
  authorOptions: [],
  sourceOptions: [],
  createdByOptions: [],
  gradingMethodOptions: [],
  chapterOptions: [],
  yearOptions: [],
  difficultyOptions: [],
  typeOptions: [],
  attemptMethodOptions: [],
  notationsOptions: [],
  termsAndConditionsOptions: [],
  statusOptions: [],
};

const getMetaDataCollection = (metaDataPayload, sectionName) => {
  if (!metaDataPayload) {
    return [];
  }

  if (Array.isArray(metaDataPayload?.[sectionName])) {
    return metaDataPayload[sectionName];
  }

  if (Array.isArray(metaDataPayload?.data)) {
    const section = metaDataPayload.data.find((item) => Array.isArray(item?.[sectionName]));
    return section?.[sectionName] || [];
  }

  return [];
};

const mapOptions = (dataArray, idKey, labelKeys = [], labelBuilder) =>
  (dataArray || [])
    .map((item) => {
      const customLabel = typeof labelBuilder === 'function' ? labelBuilder(item) : null;
      const labelKey = labelKeys.find((key) => item?.[key] !== undefined && item?.[key] !== null);
      const fallbackLabel = labelKey ? item?.[labelKey] : null;
      const label = customLabel ?? fallbackLabel;

      if (!item || item[idKey] === undefined || item[idKey] === null || label === undefined || label === null || String(label).trim() === '') {
        return null;
      }

      return {
        value: String(item[idKey]),
        label: String(label),
        meta: item,
      };
    })
    .filter(Boolean);

const buildMetaDataOptions = (metaData) => {
  
  if (!metaData) {
    return EMPTY_OPTIONS;
  }

  return {
    gradeOptions: mapOptions(getMetaDataCollection(metaData, 'Grade'), 'grade_id', ['grade']),
    subjectOptions: mapOptions(getMetaDataCollection(metaData, 'Subject'), 'subject_id', ['subject']),
    languageOptions: mapOptions(getMetaDataCollection(metaData, 'Language'), 'language_id', ['language']),
    examCategoryOptions: mapOptions(getMetaDataCollection(metaData, 'Exam_Category'), 'exam_category_id', ['name']),
    termOptions: mapOptions(getMetaDataCollection(metaData, 'Term'), 'term_id', ['term']),
    authorOptions: mapOptions(getMetaDataCollection(metaData, 'Author'), 'author_id', ['author']),
    sourceOptions: mapOptions(getMetaDataCollection(metaData, 'Source'), 'source_id', ['source']),
    createdByOptions: mapOptions(getMetaDataCollection(metaData, 'Created_By'), 'created_by_id', ['first_name', 'user_code'],
      (item) => {
        const firstName = item?.first_name || item?.firstname || item?.firstName || '';
        const lastName = item?.last_name || item?.lastname || item?.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const code = item?.user_code ? ` (${item.user_code})` : '';
        if (fullName) {
          return `${fullName}${code}`;
        }
        if (firstName) {
          return `${firstName}${code}`;
        }
        return item?.user_code || '';
      }
    ),
    assignDrawerCreatedByOptions: mapOptions(getMetaDataCollection(metaData, 'Created_By'), 'user_id', ['first_name', 'user_code'],
      (item) => {
        const firstName = item?.first_name || item?.firstname || item?.firstName || '';
        const lastName = item?.last_name || item?.lastname || item?.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const code = item?.user_code ? ` (${item.user_code})` : '';
        if (fullName) {
          return `${fullName}${code}`;
        }
        if (firstName) {
          return `${firstName}${code}`;
        }
        return item?.user_code || '';
      }
    ),
    gradingMethodOptions: mapOptions(getMetaDataCollection(metaData, 'Grading_Method'), 'grading_method_id', ['method_name']),
    chapterOptions: mapOptions(getMetaDataCollection(metaData, 'Chapter'), 'chapter_id', ['chapter']),
    yearOptions: mapOptions(getMetaDataCollection(metaData, 'Year'), 'year_id', ['year']),
    difficultyOptions: mapOptions(getMetaDataCollection(metaData, 'Difficulty'), 'difficulty_id', ['difficulty_type']),
    typeOptions: mapOptions(getMetaDataCollection(metaData, 'Type'), 'type_id', ['qtype']),
    attemptMethodOptions: mapOptions(getMetaDataCollection(metaData, 'Attempt_Method'), 'attempt_no_id', ['no_of_attempts']),
    notationsOptions: mapOptions(getMetaDataCollection(metaData, 'notations'), 'notation_id', ['notation_type']),
    termsAndConditionsOptions: getMetaDataCollection(metaData, 'Terms_And_Conditions'),
    StatusOptions: mapOptions(getMetaDataCollection(metaData, 'Status'), 'status_id', ['status'],
      (item) => {
        const rawStatus = item?.status || '';
        const displayMapping = {
          Initial: "Initial",
          Ready: "Ready",
          Review: "Review",
          Published: "Published",
          Retired: "Retired",
          InReview: "InReview",
          ReferBack: "Refer Back",
          ReviewCompleted: "Review Completed",
          ReadyToPublish: "Ready to Publish",
          ProofReading: "Proofreading",
          DataEntryVerification: "Data Entry Verification",
        };
        return displayMapping[rawStatus] || rawStatus;
      }
    ),
    ReviewersOptions: mapOptions(getMetaDataCollection(metaData, 'Reviewers'), 'teacher_id', ['first_name', 'user_code'],
      (item) => {
        const firstName = item?.first_name || item?.firstname || item?.firstName || '';
        const lastName = item?.last_name || item?.lastname || item?.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const code = item?.user_code ? ` (${item.user_code})` : '';
        if (fullName) {
          return `${fullName}${code}`;
        }
        if (firstName) {
          return `${firstName}${code}`;
        }
        return item?.user_code || '';
      }
    ),
  };
};
//----------------------------------------------------------------------------------------------------------------


// -------------------------------------------------------------------------
// Fetch assessment data whenever filters change
//--------------------------------------------------------------------------

const INITIAL_FILTERS = {
    limit: 20,
    offset: 0,
    sort_column: "created_at",
    sort_direction: "DESC",
    search_term: "",
    selectedAuthors: [],
    selectedChapters: [],
    selectedGrades: [],
    selectedDifficulties: [],
    selectedLanguages: [],
    selectedTerms: [],
    selectedYears: [],
    selectedsubjects: [],
    selectedStatuses: [],
    selectedExamCategories: [],
    selectedSources: [],
    selectedCreators: [],
    selectedStartDate: null,
    selectedEndDate: null,
};


export function AssessLibSectionsProvider({ children }) {
  const [metaData, setMetaData] = useState(null);
  const [isMetaDataLoading, setIsMetaDataLoading] = useState(false);
  const [metaDataError, setMetaDataError] = useState(null);

  const [isViewingPaper, setIsViewingPaper] = useState(false);

  const [activeTab, setActiveTab] = useState(() =>
    sessionStorage.getItem('employeeType') === 'MASAD' ? 'all' : 'my'
  );
  const [allFilters, setAllFilters] = useState({ ...INITIAL_FILTERS });
  const [myFilters, setMyFilters] = useState({ ...INITIAL_FILTERS });

  const location = useLocation();
  const [navApplied, setNavApplied] = useState(false);

  useEffect(() => {
    if (!navApplied && location?.state) {
      const navStatus = location.state.value ?? location.state.selectedStatusFilter?.value ?? location.state.selectedStatusFilter;
      if (navStatus) {
        setNavApplied(true);
        const navStatusArray = Array.isArray(navStatus) ? navStatus : [navStatus];
        setMyFilters((prev) => ({
          ...prev,
          selectedStatuses: navStatusArray,
          offset: 0,
        }));
        setAllFilters((prev) => ({
          ...prev,
          selectedStatuses: navStatusArray,
          offset: 0,
        }));
      }
    }
  }, [location, navApplied]);

  const [AllassessmentData, setAllassessmentData] = useState([]);
   const [totalAssessCount, setAllassessTotalCount] = useState(0);
  const [isAllAssessmentDataLoading, setIsAllAssessmentDataLoading] = useState(false);
  const [allAssessmentDataError, setAllAssessmentDataError] = useState(null);

  const [myAssessmentData, setMyAssessmentData] = useState([]);
  const [myAssessTotalCount, setMyAssessTotalCount] = useState(0);
  const [isMyAssessmentDataLoading, setIsMyAssessmentDataLoading] = useState(false);
  const [myAssessmentDataError, setMyAssessmentDataError] = useState(null);

  // Each list has independent requests. These counters ensure an older response
  // cannot overwrite results returned for a newer filter or pagination request.
  const myPapersRequestIdRef = useRef(0);
  const allPapersRequestIdRef = useRef(0);

  const employeeType = sessionStorage.getItem('employeeType');
  const user_id = sessionStorage.getItem('manage_user_id');
 


// -------------------------------------------------------------------------
// Fetch metadata once on mount
// ------------------------------------------------------------------------
  const metaDataOptions = useMemo(() => buildMetaDataOptions(metaData), [metaData]);

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        setIsMetaDataLoading(true);
        setMetaDataError(null);
        const data = await getMetaDataForFilter();
        setMetaData(data || null);
      } catch (error) {
        setMetaDataError(error);
      } finally {
        setIsMetaDataLoading(false);
      }
    };

    fetchMetaData();
  }, []);

// -------------------------------------------------------------------------
// Fetch assessment data whenever filters change
//-------------------------------------------------------------------------

  useEffect(() => {
    if (employeeType == 'MASAD') {
      return;
    }
    const requestId = ++myPapersRequestIdRef.current;
    let isActive = true;
    const isCurrentRequest = () =>
      isActive && requestId === myPapersRequestIdRef.current;

    const fetchMyAssessmentData = async () => {
      try {
        setIsMyAssessmentDataLoading(true);
        setMyAssessmentDataError(null);
       
        if (!user_id) {
          if (isCurrentRequest()) {
            setMyAssessmentData([]);
            setMyAssessTotalCount(0);
          }
          return;
        }

        let response;
         if (employeeType === 'TEACH') {
          response = await getTeacherAssignedAssessments(myFilters, user_id);
        } else {
          response = await getDEOPAssignedAssessments(myFilters, user_id);
        }

        if (!isCurrentRequest()) return;

        const assessments = response?.data?.assessments || [];
        console.log("[MY PAPERS API]", {
          employeeType,
          user_id,
          count: assessments.length,
          assessments,
        });
        const count = response?.data?.total_all_count || 0;

        setMyAssessmentData(assessments);
        setMyAssessTotalCount(count);
      } catch (error) {
        if (isCurrentRequest()) {
          setMyAssessmentDataError(error);
        }
      } finally {
        if (isCurrentRequest()) {
          setIsMyAssessmentDataLoading(false);
        }
      }
    };

    fetchMyAssessmentData();
    return () => {
      isActive = false;
    };
  }, [myFilters, employeeType, isViewingPaper]);

 
  useEffect(() => {
    if (employeeType == 'TEACH') {
      return;
    }
    const requestId = ++allPapersRequestIdRef.current;
    let isActive = true;
    const isCurrentRequest = () =>
      isActive && requestId === allPapersRequestIdRef.current;

    const fetchAllAssessmentData = async () => {
      try {
        setIsAllAssessmentDataLoading(true);
        setAllAssessmentDataError(null);
        const isPaperReviewManagedRole = employeeType === 'MASAD' || employeeType === 'DEOP';
        const response = await getAllAssessmentsForPaperReview(
          allFilters,
          isPaperReviewManagedRole ? user_id : null,
          isPaperReviewManagedRole ? employeeType : null
        );
        if (!isCurrentRequest()) return;

        const assessments = response?.data?.assessments || [];

        console.log("[ALL PAPERS API]", {
          employeeType,
          count: assessments.length,
          assessments,
        });


        const count = response?.data?.total_all_count || 0;

        setAllassessmentData(assessments);
        setAllassessTotalCount(count);
      } catch (error) {
        if (isCurrentRequest()) {
          setAllAssessmentDataError(error);
        }
      } finally {
        if (isCurrentRequest()) {
          setIsAllAssessmentDataLoading(false);
        }
      }
    };

    fetchAllAssessmentData();
    return () => {
      isActive = false;
    };
  }, [allFilters, employeeType, isViewingPaper]);

  

  const filters = activeTab === 'my' ? myFilters : allFilters;
  const setFilters = useCallback((updater) => {
    const applyUpdate = (prev) =>
      typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };

    if (activeTab === 'my') {
      setMyFilters((prev) => applyUpdate(prev));
      return;
    }

    setAllFilters((prev) => applyUpdate(prev));
  }, [activeTab]);

  const onPageChange = useCallback((page) => {
    setFilters((prev) => ({
      ...prev,
      offset: (page - 1) * prev.limit,
    }));
  }, [setFilters]);

  const onPageSizeChange = useCallback((newPageSize) => {
    setFilters((prev) => ({
      ...prev,
      limit: newPageSize,
      offset: 0,
    }));
  }, [setFilters]);

  const isAssessmentDataLoading = activeTab === 'my' ? isMyAssessmentDataLoading : isAllAssessmentDataLoading;
  const assessmentDataError = activeTab === 'my' ? myAssessmentDataError : allAssessmentDataError;

  const value = useMemo(
    () => ({
      metaData,
      metaDataOptions,
      isMetaDataLoading,
      metaDataError,

      activeTab,
      setActiveTab,
      filters,
      setFilters,
      
      AllassessmentData,
      myAssessmentData,
      isAssessmentDataLoading,
      assessmentDataError,
      totalAssessCount,
      myAssessTotalCount,
      onPageChange,
      onPageSizeChange,

      employeeType,
      isViewingPaper,
      setIsViewingPaper,
    })
   , [
      metaData,
      metaDataOptions,
      isMetaDataLoading,
      metaDataError,
      activeTab,
      filters,
      AllassessmentData,
      myAssessmentData,
      isAssessmentDataLoading,
      assessmentDataError,
      totalAssessCount,
      myAssessTotalCount,
      setFilters,
      onPageChange,
      onPageSizeChange,
      isViewingPaper,
    ]
  );

  return (
    <AssessLibSectionsContext.Provider value={value}>
      {children}
    </AssessLibSectionsContext.Provider>
  );
}

export default AssessLibSectionsProvider;

